import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormInstance, RuleObject } from 'antd/es/form';
import dayjs from 'dayjs';

import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  RowSize,
  SelectForm,
} from '../../../../../../../shared/components';
import { SearchBarPayment } from './components/SearchBarPayment';

import { GenericDataFormContext, QuotationInfoContext, StepOneFormContext } from '../../contexts';

import { PAYMENT_FORM_CODE } from '../../interfaces';
import {
  PaymentMethodForms,
  SelectOption,
  TarjetasDelAsegurado,
} from '../../../../../../../shared/interfaces';

import {
  ValidateForm,
  validateLengthCreditCard,
} from '../../../../../../../shared/utils';

import './index.css';
import { LoadQuotationToRenewResponse } from '../../interfaces/load-renewal-quotation-response-interface';

interface PaymentMethodProps {
  paymentMethodsForm?: FormInstance;
  setMethodSelectedFromStepThree: Dispatch<
    SetStateAction<PaymentMethodForms | undefined>
  >;
}

const formatExpiredCardDate = (month: number, year: number) => {
  const formattedMonth = month < 10 ? `0${month}` : `${month}`;
  return `${formattedMonth}/${year}`;
};

export const PaymentMethodSection = ({
  paymentMethodsForm,
  setMethodSelectedFromStepThree,
}: PaymentMethodProps) => {
  const validateForm = new ValidateForm();

  const {
    paymentMethodsList,
    listCreditCardInsured,
    paymentMethodSectionForm,
    setPaymentMethodFormValues,
    getListCreditCardByInsured,
    getListBankAccountByInsured,
    getListCreditCards,
    listCreditCards,
    listBankAccountByInsured,
    isLoadingListCreditCards,
    isLoadingPaymentMethods,
    setListBankAccountByInsured,
    setListCreditCardInsured,
  } = useContext(GenericDataFormContext);

  const { clientSelected } = useContext(StepOneFormContext);

  const { quotationToRenewal, savedQuotation } = useContext(QuotationInfoContext);
  const formaDePago = quotationToRenewal?.formaDePago || savedQuotation?.formaDePago;

  const [disableCompanySelect, setDisableCompanySelect] =
    useState<boolean>(false);

  const [companySelectedFiltered, setCompanySelectedFiltered] =
    useState<SelectOption>();

  const selectedPaymentMethod = useMemo(
    () => paymentMethodsForm?.getFieldValue('paymentMethod'),
    [paymentMethodsForm]
  );

  const paymentMethodSelectedFromStepThree = useMemo(
    () =>
      paymentMethodsList?.find(
        (methodPayment) =>
          `${methodPayment?.codigoFormaDePago}-${methodPayment?.nroPlanDePago}` === selectedPaymentMethod
      ),
    [paymentMethodsList, selectedPaymentMethod]
  );

  const setPaymentMethod = useCallback(() => {
    paymentMethodSectionForm?.setFieldValue(
      'paymentMethod',
      paymentMethodSelectedFromStepThree?.descripcion
    );
  }, [paymentMethodSectionForm, paymentMethodSelectedFromStepThree]);

  const onFinish = useCallback(() => {
    const formFields = paymentMethodSectionForm?.getFieldsValue();
    setPaymentMethodFormValues(formFields);
  }, [paymentMethodSectionForm, setPaymentMethodFormValues]);

  const getCompanySelected = useCallback(() => {
    const companySelected =
      paymentMethodSectionForm?.getFieldValue('companyCreditCard');
    const companyFiltered = listCreditCards?.find(
      (card: SelectOption) => card.value === companySelected
    );
    setCompanySelectedFiltered(companyFiltered);
    setDisableCompanySelect(true);
  }, [listCreditCards]);

  const onSelectCompany = useCallback(
    async (companySelected: number) => {
      try {
        const companyFiltered = listCreditCards?.find(
          (card: SelectOption) => Number(card.value) === companySelected
        );
        setCompanySelectedFiltered(companyFiltered);

        await paymentMethodSectionForm?.validateFields([
          'cardNumber',
          'companyCreditCard',
        ]);
      } catch (error) {}
    },
    [listCreditCards, setCompanySelectedFiltered, paymentMethodSectionForm]
  );

  const handlerOnChange = useCallback(
    async (value: string) => {
      try {
        if (listBankAccountByInsured) {
          paymentMethodSectionForm?.setFieldValue('cbuNumber', value);
          await paymentMethodSectionForm?.validateFields(['cbuNumber']);
          return;
        }

        if (listCreditCardInsured) {
          paymentMethodSectionForm?.setFieldValue('cardNumber', value);

          const creditCardFiltered =
            listCreditCardInsured?.tarjetasDelAsegurado.find(
              (card: TarjetasDelAsegurado) => card.numeroTarjeta === value
            );

          getCompanySelected();

          if (creditCardFiltered) {
            setDisableCompanySelect(true);

            const dateExpiredCreditCard = formatExpiredCardDate(
              creditCardFiltered.mesHasta,
              creditCardFiltered.anioHasta
            );

            paymentMethodSectionForm?.setFieldValue(
              'expiration',
              dayjs(dateExpiredCreditCard, 'MM/YYYY')
            );

            paymentMethodSectionForm?.setFieldValue(
              'companyCreditCard',
              creditCardFiltered?.idEmpresa
            );

            await paymentMethodSectionForm?.validateFields([
              'cardNumber',
              'companyCreditCard',
              'expiration',
            ]);

            return;
          }

          paymentMethodSectionForm?.setFieldValue('expiration', undefined);
          paymentMethodSectionForm?.setFieldValue('companyCreditCard', null);
          setDisableCompanySelect(false);

          await paymentMethodSectionForm?.validateFields([
            'cardNumber',
            'companyCreditCard',
            'expiration',
          ]);
        }
      } catch (error) {}
    },
    [
      listBankAccountByInsured,
      listCreditCardInsured,
      paymentMethodSectionForm,
      getCompanySelected,
      setDisableCompanySelect,
    ]
  );

  const handleSelect = useCallback(async () => {
    try {
      if (listCreditCardInsured) getCompanySelected();

      await paymentMethodSectionForm?.validateFields([
        'cbuNumber',
        'cardNumber',
        'companyCreditCard',
        'expiration',
      ]);

      return;
    } catch (error) {}
  }, [listCreditCardInsured, getCompanySelected, paymentMethodSectionForm]);

  const creditCardValidator = useCallback(
    (_: RuleObject, value: any) => {
      // Si no hay valor, dejar pasar (campo requerido lo maneja el otro validador)
      if (!value) {
        return Promise.resolve();
      }

      // Obtener la empresa seleccionada del formulario en tiempo real
      const companyIdSelected = paymentMethodSectionForm?.getFieldValue('companyCreditCard');
      
      // Si no hay empresa seleccionada aún, permitir temporalmente
      if (!companyIdSelected) {
        return Promise.resolve();
      }

      // Buscar los datos de la empresa seleccionada
      const companyData = listCreditCards?.find(
        (card: SelectOption) => card.value === companyIdSelected
      );

      // Si no encontramos la empresa, permitir (se validará cuando se seleccione)
      if (!companyData?.cantidadDeDigitos) {
        return Promise.resolve();
      }

      if (
        validateLengthCreditCard(
          value.length,
          companyData.cantidadDeDigitos
        )
      ) {
        return Promise.resolve();
      }

      return Promise.reject(
        new Error('Debe ingresar un número de tarjeta valido.')
      );
    },
    [paymentMethodSectionForm, listCreditCards]
  );

  const initialCompanyValue = useMemo(() => {
    if (!formaDePago?.empresaTarjetaCredito || !listCreditCards) return undefined;
  
    const company = listCreditCards.find(
      card => card.label === formaDePago.empresaTarjetaCredito
    );
  
    return company?.value;
  }, [formaDePago?.empresaTarjetaCredito, listCreditCards]);
  

  useEffect(() => {
    setPaymentMethod();
    setMethodSelectedFromStepThree(paymentMethodSelectedFromStepThree);
  }, []);

  useEffect(() => {
    if (!initialCompanyValue) return;
  
    paymentMethodSectionForm?.setFieldValue(
      'companyCreditCard',
      initialCompanyValue
    );
  
    getCompanySelected();
  }, [initialCompanyValue]);
  

  useEffect(() => {
    if (
      paymentMethodSelectedFromStepThree?.codigoFormaDePago ===
      PAYMENT_FORM_CODE.TARJETA_CREDITO
    ) {
      getListCreditCardByInsured(clientSelected?.nroAsegurado!);
      getListCreditCards();
      setListBankAccountByInsured(undefined);
    }

    if (
      paymentMethodSelectedFromStepThree?.codigoFormaDePago === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CAJA_AHORRO ||
      paymentMethodSelectedFromStepThree?.codigoFormaDePago === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CUENTA_CORRIENTE
    ) {
      getListBankAccountByInsured(clientSelected?.nroAsegurado!);
      setListCreditCardInsured(undefined);
    }
  }, []);

  return (
    <Form
      formProps={{
        form: paymentMethodSectionForm,
        name: 'payment-method-form',
        onChange: onFinish,
        onClick: onFinish,
      }}
    >
      <ContentSection level='h3' title='Método de pago'>
        <RowSize>
          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'Forma de Pago:',
                name: 'paymentMethod',
                rules: validateForm.generateRules({
                  label: 'forma de pago',
                  rules: [],
                }),
              }}
              selectProps={{
                disabled: true,
                loading: isLoadingPaymentMethods,
                options: paymentMethodsList,
              }}
            />
          </ColSize>
          {
          (
            paymentMethodSelectedFromStepThree?.codigoFormaDePago === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CAJA_AHORRO ||
            paymentMethodSelectedFromStepThree?.codigoFormaDePago === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CUENTA_CORRIENTE
          ) && (
            <ColSize size={25}>
              <SearchBarPayment
                formProps={{
                  label: 'Número de CBU:',
                  name: 'cbuNumber',
                  rules: [
                    ...validateForm.generateRules({
                      label: 'número de CBU',
                      rules: ['required'],
                    }),
                    {
                      validator: (_: any, value: string) => {
                        if (!value) return Promise.resolve();
                        if (!/^\d{22}$/.test(value))
                          return Promise.reject(new Error('El CBU debe tener 22 dígitos numéricos.'));
                        return Promise.resolve();
                      },
                    },
                  ],
                  initialValue: formaDePago?.cuentaBancaria ?? undefined
                }}
                handlerOnChange={handlerOnChange}
                handleSelect={handleSelect}
                paymentMethodSelected={paymentMethodSelectedFromStepThree}
              />
            </ColSize>
          )}
        </RowSize>

        {paymentMethodSelectedFromStepThree?.codigoFormaDePago ===
          PAYMENT_FORM_CODE.TARJETA_CREDITO && (
          <RowSize>
            <ColSize size={25}>
              <SearchBarPayment
                formProps={{
                  label: 'Número de Tarjeta:',
                  name: 'cardNumber',
                  rules: [
                    ...validateForm.generateRules({
                      label: 'número de tarjeta',
                      rules: ['required'],
                    }),
                    {
                      validator: creditCardValidator,
                    },
                  ],
                  initialValue: formaDePago?.tarjetaDeCredito ?? undefined
                }}
                getCompanySelected={getCompanySelected}
                handlerOnChange={handlerOnChange}
                handleSelect={handleSelect}
                paymentMethodSelected={paymentMethodSelectedFromStepThree}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Empresa:',
                  name: 'companyCreditCard',
                  rules: validateForm.generateRules({
                    label: 'empresa',
                    rules: ['required'],
                  }),
                  initialValue: initialCompanyValue
                }}
                selectProps={{
                  disabled: disableCompanySelect,
                  loading: isLoadingListCreditCards,
                  onSelect: onSelectCompany,
                  options: listCreditCards,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  label: 'Vencimiento:',
                  name: 'expiration',
                  rules: validateForm.generateRules({
                    label: 'vencimiento',
                    rules: ['required'],
                  }),
                  initialValue: formaDePago?.fechaVencimiento ? dayjs(formaDePago.fechaVencimiento, 'YYYYMM') : null,                
                }}
                datePickerProps={{
                  format: 'MM/YYYY',
                  minDate: dayjs(),
                  picker: 'month',
                  placeholder: 'mm/yyyy',
                }}
              />
            </ColSize>
          </RowSize>
        )}
      </ContentSection>
    </Form>
  );
};
